import React, { useEffect, useRef, useState } from "react";
import Button from "../Button";
import { Calendar } from "lucide-react";
import CalendarView from "./CalendarView";

interface DateRowViewProps {
  onDateSelect: (date: Date) => void;
  selectedDate: Date | null;
  displayedMonth: { month: number; year: number };
}

const DateRowView: React.FC<DateRowViewProps> = ({
  onDateSelect,
  selectedDate,
  displayedMonth,
}) => {
  const [showFullCalendar, setShowFullCalendar] = useState(false);
  const selectedDateRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (selectedDateRef.current) {
      selectedDateRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [selectedDate]);

  const generateMonthDays = (
    month: number,
    year: number,
    selectedDate: Date | null
  ) => {
    const days: {
      date: number;
      day: string;
      fullDate: Date;
      isSelected: boolean;
      isDisabled: boolean;
    }[] = [];

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Remove time for accurate comparison
    const date = new Date(year, month, 1);

    while (date.getMonth() === month) {
      const fullDate = new Date(date);
      fullDate.setHours(0, 0, 0, 0); // Remove time for accurate comparison
      const day = fullDate.toLocaleDateString("en-US", { weekday: "short" }); // e.g. "Mon"
      days.push({
        date: fullDate.getDate(),
        day,
        fullDate, // Store the full date for comparison and selection
        isSelected: selectedDate?.toDateString() === fullDate.toDateString(),
        isDisabled: fullDate < today,
      });
      date.setDate(date.getDate() + 1);
    }

    return days;
  };

  const monthDays = generateMonthDays(
    displayedMonth.month,
    displayedMonth.year,
    selectedDate
  );

  return (
    <>
      <div className="flex items-center gap-3">
        <div className="flex-1 overflow-x-auto">
          <div className="flex gap-2 pb-2">
            {monthDays.map((dateItem) => (
              <button
                key={dateItem.fullDate.toISOString()}
                ref={dateItem.isSelected ? selectedDateRef : null}
                onClick={() => onDateSelect(dateItem.fullDate)}
                disabled={dateItem.isDisabled}
                className={`flex-shrink-0 w-16 h-16 rounded-lg border-2 flex flex-col items-center justify-center transition-all ${
                  dateItem.isSelected
                    ? "border-[var(--app-primary)] bg-white"
                    : "border-gray-200 bg-white hover:border-gray-300"
                } ${
                  dateItem.isDisabled
                    ? "opacity-50 cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200"
                    : ""
                }`}
              >
                <span
                  className={`text-lg font-bold ${
                    dateItem.isSelected
                      ? "text-[var(--app-primary)]"
                      : "text-gray-800"
                  }`}
                >
                  {dateItem.date}
                </span>
                <span
                  className={`text-xs ${
                    dateItem.isSelected
                      ? "text-[var(--app-primary)]"
                      : "text-gray-500"
                  }`}
                >
                  {dateItem.day}
                </span>
              </button>
            ))}
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => setShowFullCalendar(!showFullCalendar)}
        >
          <Calendar className="w-4 h-4" />
        </Button>
      </div>
      {showFullCalendar && (
        <CalendarView selectedDate={selectedDate} onDateSelect={onDateSelect} />
      )}
    </>
  );
};

export default DateRowView;
